/**
 * Phase 20 Plan 20-02 — AlertsListPage
 *
 * Route: /alerts
 * Permission: notifications.view
 *
 * Full paginated alert list with filters, bulk actions, and detail dialog.
 */

import { useState, type JSX } from 'react';

import { AlertDetailDialog } from '@/components/notifications/AlertDetailDialog';
import { AlertFilterBar } from '@/components/notifications/AlertFilterBar';
import { AlertsBulkActions } from '@/components/notifications/AlertsBulkActions';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { useAcknowledgeMutation, useAlertsQuery, useDismissMutation } from '@/hooks/useNotificationsQueries';
import { useAuth } from '@/lib/auth/AuthContext';
import { formatRelative } from '@/lib/utils/relative-time';
import type { AlertDto, AlertListQuery, AlertSeverity } from '@/types/notifications';

const DEFAULT_FILTERS: AlertListQuery = { status: 'New', page: 1, pageSize: 20 };

function severityVariant(s: AlertSeverity): 'default' | 'secondary' | 'destructive' {
  if (s === 'Critical') return 'destructive';
  if (s === 'Warning') return 'secondary';
  return 'default';
}

export function AlertsListPage(): JSX.Element {
  const { permissions } = useAuth();
  const canAck = permissions.includes('notifications.acknowledge');
  const canDismiss = permissions.includes('notifications.dismiss');

  const [filters, setFilters] = useState<AlertListQuery>(DEFAULT_FILTERS);
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());
  const [detailAlert, setDetailAlert] = useState<AlertDto | null>(null);

  const alertsQuery = useAlertsQuery(filters);
  const ackMutation = useAcknowledgeMutation();
  const dismissMutation = useDismissMutation();

  const items = alertsQuery.data?.items ?? [];
  const total = alertsQuery.data?.total ?? 0;
  const currentPage = filters.page ?? 1;
  const pageSize = filters.pageSize ?? 20;
  const totalPages = Math.max(1, Math.ceil(total / pageSize));

  const handleFilterChange = (f: AlertListQuery): void => {
    setFilters({ ...f, page: 1 });
    setSelectedIds(new Set());
  };

  const toggleSelect = (id: number): void => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const clearSelection = (): void => { setSelectedIds(new Set()); };

  const goToPage = (page: number): void => {
    setFilters((prev) => ({ ...prev, page }));
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold tracking-tight">Alerts</h1>
        <p className="text-muted-foreground">View and manage your notification alerts.</p>
      </div>

      <AlertFilterBar filters={filters} onChange={handleFilterChange} />

      {selectedIds.size > 0 && (
        <AlertsBulkActions
          selectedIds={Array.from(selectedIds)}
          onClearSelection={clearSelection}
        />
      )}

      <Card>
        <CardHeader>
          <CardTitle>Alerts ({total})</CardTitle>
        </CardHeader>
        <CardContent>
          {alertsQuery.isLoading && (
            <p className="text-sm text-muted-foreground" data-testid="alerts-loading">
              Loading alerts…
            </p>
          )}
          {alertsQuery.isError && (
            <p className="text-sm text-destructive" data-testid="alerts-error">
              Failed to load alerts.
            </p>
          )}

          <div className="overflow-x-auto">
          <Table data-testid="alerts-list-table">
            <TableHeader>
              <TableRow>
                <TableHead className="w-8" />
                <TableHead>Severity</TableHead>
                <TableHead>Type</TableHead>
                <TableHead>Message</TableHead>
                <TableHead>Created</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {!alertsQuery.isLoading && items.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} className="py-8 text-center">
                    <span className="text-muted-foreground" data-testid="alerts-empty">
                      No alerts yet.
                    </span>
                  </TableCell>
                </TableRow>
              ) : (
                items.map((alert) => (
                  <TableRow key={alert.id} data-testid={`alert-row-${String(alert.id)}`}>
                    <TableCell>
                      <Checkbox
                        checked={selectedIds.has(alert.id)}
                        onCheckedChange={() => { toggleSelect(alert.id); }}
                        data-testid={`alert-row-checkbox-${String(alert.id)}`}
                        aria-label={`Select alert ${String(alert.id)}`}
                      />
                    </TableCell>
                    <TableCell>
                      <Badge variant={severityVariant(alert.severity)}>
                        {alert.severity}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-sm">{alert.notificationType}</TableCell>
                    {/* Client feedback (2026-09): the cell stays truncated so the table
                        keeps its shape, but the full message is now reachable on hover as
                        well as through the detail dialog. The Type cell above deliberately
                        keeps the raw enum, because the type filter offers those exact
                        strings and the two must read alike. */}
                    <TableCell className="max-w-xs truncate text-sm" title={alert.message}>
                      {alert.message}
                    </TableCell>
                    <TableCell className="text-sm text-muted-foreground">
                      {formatRelative(alert.createdAt)}
                    </TableCell>
                    <TableCell>
                      <Badge variant={alert.status === 'New' ? 'default' : 'secondary'}>
                        {alert.status}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1">
                        <Button
                          type="button"
                          size="sm"
                          variant="ghost"
                          onClick={() => { setDetailAlert(alert); }}
                          data-testid={`alert-detail-btn-${String(alert.id)}`}
                        >
                          Detail
                        </Button>
                        {canAck && alert.status === 'New' && (
                          <Button
                            type="button"
                            size="sm"
                            variant="outline"
                            onClick={() => { ackMutation.mutate(alert.id); }}
                            disabled={ackMutation.isPending}
                            data-testid={`alert-ack-btn-${String(alert.id)}`}
                          >
                            Ack
                          </Button>
                        )}
                        {canDismiss && alert.status !== 'Dismissed' && (
                          <Button
                            type="button"
                            size="sm"
                            variant="ghost"
                            onClick={() => { dismissMutation.mutate(alert.id); }}
                            disabled={dismissMutation.isPending}
                            data-testid={`alert-dismiss-btn-${String(alert.id)}`}
                          >
                            Dismiss
                          </Button>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-end gap-2 pt-4">
              <Button
                type="button"
                size="sm"
                variant="outline"
                disabled={currentPage <= 1}
                onClick={() => { goToPage(currentPage - 1); }}
                data-testid="pagination-prev"
              >
                Previous
              </Button>
              <span className="text-sm text-muted-foreground">
                Page {currentPage} of {totalPages}
              </span>
              <Button
                type="button"
                size="sm"
                variant="outline"
                disabled={currentPage >= totalPages}
                onClick={() => { goToPage(currentPage + 1); }}
                data-testid="pagination-next"
              >
                Next
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Detail dialog */}
      {detailAlert !== null && (
        <AlertDetailDialog
          open
          alert={detailAlert}
          onClose={() => { setDetailAlert(null); }}
        />
      )}
    </div>
  );
}
